import React, { useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { Box, Button, Theme, Typography } from "@material-ui/core";
import Backdrop from "@material-ui/core/Backdrop";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Snackbar from "@material-ui/core/Snackbar";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Alert from "@material-ui/lab/Alert";
import { CSSProperties } from "@material-ui/styles";
import clsx from "clsx";
import { useFormik } from "formik";
import { encode } from "js-base64";
import { object, SchemaOf, string } from "yup";
import { loginAction } from "src/app-redux/auth/actions/authActions";
import {
  isLogin,
  resetErrorAction,
  selectAuthError,
  selectResetEmail,
  selectStatus,
  setIsLoggedIn,
  setPayloadValues,
} from "src/app-redux/auth/authSlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import NLCVideo from "src/assets/login-video.mp4"; // Import video file
import { ManagementRoute } from "src/modules/management/management.routes";
import { VerifyOTP } from "./verifyOTP";
import NLCLogo from "src/assets/images/logo_login.png";
import CommonLoader from "src/components/Loader/CommonLoader";

const mediumText: CSSProperties = {
  fontWeight: 500,
} as const;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footNote: {
      display: "block",
      // maxWidth: "320px",
      margin: "auto",
      fontSize: "12px",
      color: "#96A4B9",
      marginTop: "30px",
      textAlign: "left",
    },
    forgotPasswordButton: {
      paddingTop: "0",
      fontSize: "12px",
      color: "#488CFB",
      textTransform: "capitalize",
      "&:hover": {
        backgroundColor: "transparent",
        textDecoration: "underline",
      },
    },
    backdrop: {
      zIndex: theme.zIndex.modal + 1,
      color: "#fff",
    },
    headerLogo: {
      textAlign: "center",
    },
    headerLogoLink: {
      textAlign: "center",
      width: "100%",
      float: "left",
    },
  })
);

interface IForm {
  password: string;
  username: string;
}
const validationSchema: SchemaOf<IForm> = object({
  username: string()
    .trim()
    .max(50, "Username should not exceed more than 50 characters")
    .required("Username is required"),
  password: string()
    .trim()
    .max(50, "Password should not exceed more than 50 characters")
    .required("Password is required"),
});
export function LoginPage() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  useEffect(() => {
    dispatch(resetErrorAction());
  }, []);

  const authStatus = useAppSelector(selectStatus);
  const resetEmail = useAppSelector(selectResetEmail);
  const authError = useAppSelector(selectAuthError);
  const user = useAppSelector(isLogin);

  const [state, setState] = useState({ showPassword: false });
  const [alert1, setAlert] = useState(Boolean(authStatus === "failed" && authError));
  const [verifyOTP, setVerifyOTP] = useState<boolean>(false);

  useEffect(() => {
    if (!authError) setAlert(false);
  }, [authStatus, authError]);

  useEffect(() => {
    localStorage.getItem("showOtpForm") ? setVerifyOTP(true) : setVerifyOTP(false);
  }, [verifyOTP]);

  const handleClose = (_event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") return;
    setAlert(false);
  };
  const handleClickShowPassword = () => {
    setState({ ...state, showPassword: !state.showPassword });
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  const formik = useFormik<IForm>({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const model = {
        username: values.username,
        password: values.password,
        newPassword: "",
      };
      const res = await dispatch(loginAction(model)); // LoginAction
      const response: any = res.payload;
      if (!response?.twoFARequired) {
        dispatch(setIsLoggedIn(true));
        const menus: any = [...response.userMenus];
        if (menus.length) {
          localStorage.setItem(
            "defaultRoute",
            menus[0].routeName + menus[0].childMenus[0].routeName
          );

          const allowedRoutes: any = ["/auth/change-password"];
          menus.forEach((menuItem: any) => {
            allowedRoutes.push(menuItem.routeName);
            const arr: any[] = menuItem.childMenus;
            if (arr.length) {
              arr.forEach((subItem: any) => {
                allowedRoutes.push(menuItem.routeName + subItem.routeName);
              });
            }
          });

          localStorage.setItem("allowedRoutes", JSON.stringify(allowedRoutes));

          history.push(menus[0].routeName + menus[0].childMenus[0].routeName); // Dynamic Menus
        } else {
          localStorage.setItem("defaultRoute", "/unauthorized");
          localStorage.setItem("allowedRoutes", JSON.stringify([]));
          history.push("/unauthorized"); // Return to homescreen
        }
      } else {
        setVerifyOTP(true);
        dispatch(setPayloadValues(res.meta?.arg));
      }
    },
  });

  return authStatus === "passwordResetRequired" && resetEmail ? (
    <Redirect
      to={{ pathname: "/auth/set-password", search: `?email=${encode(resetEmail || "")}` }}
    />
  ) : user ? (
    <Redirect to={{ pathname: ManagementRoute.path }} />
  ) : (
    <>
      <Backdrop className={classes.backdrop} open={Boolean(authStatus === "loading")}>
        {/* <CircularProgress color="inherit" /> */}
        <CommonLoader />
      </Backdrop>
      <Snackbar open={alert1} autoHideDuration={4000} onClose={handleClose}>
        <Alert variant="filled" severity="error">
          Invalid credentials. Please try again.
          <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Alert>
      </Snackbar>
      <div className="app newLogin">
        <video className="background-video" autoPlay loop muted>
          <source src={NLCVideo} type="video/mp4" />
        </video>
        <div className="new-login-card">
          <div className="inner">
            <Box my={2} className="headerLogo" textAlign="Center">
              <a href="/" className="headerLogoLink">
                <img
                  className="headerLogo"
                  src={NLCLogo}
                  alt="logo"
                  width="220px"
                  style={{ marginLeft: "50px" }}
                />
              </a>
            </Box>
            {!verifyOTP ? (
              <div className="form-wrapper">
                <h4 className="form-heading">Login to your account</h4>
                <form onSubmit={formik.handleSubmit}>
                  <TextField
                    size="small"
                    fullWidth
                    className="customTextField"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="Username"
                    name="username"
                    placeholder="Enter Username"
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onKeyDownCapture={(e: any) => {
                      if (/\s/g.test(e.key)) e.preventDefault();
                    }}
                    error={formik.touched.username && Boolean(formik.errors.username)}
                    helperText={formik.touched.username && formik.errors.username}
                  />
                  <Box m={4} />
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="Password"
                    className="customTextField"
                    name="password"
                    placeholder="Enter Password"
                    type={state.showPassword ? "text" : "password"}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {state.showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box m={4} />
                  <Button
                    className="login-button"
                    type="submit"
                    variant="contained"
                    color="secondary"
                    fullWidth
                  >
                    Login
                  </Button>
                </form>
              </div>
            ) : (
              <VerifyOTP />
            )}
            <Typography
              component="span"
              variant="body1"
              color="textPrimary"
              style={mediumText}
              className={clsx(classes.footNote)}
            >
              Copyright©, NLC, {new Date().getFullYear()}
            </Typography>
          </div>
        </div>
      </div>
    </>
  );
}
